import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Typography, Select, Spin, Button, Space, message, Card, Tooltip, Switch, Upload } from 'antd';
import { ReloadOutlined, SaveOutlined, SettingOutlined, FilterOutlined, DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import api, { monthlyMetricService } from '../../services/api';
import YearlyMetricsTable from './YearlyMetricsTable';
import './styles.css';

const { Title, Text } = Typography;
const { Option } = Select;

// Türkçe dil ayarı
dayjs.locale('tr');

// Gösterilecek metrik ID'leri
// Bu diziyi istediğiniz metrik ID'leriyle doldurun
// Örnek: [10055, 10063, 10009, 10010]
// Boş bırakırsanız tüm metrikler gösterilecektir
const FILTERED_METRIC_IDS = [
  10055, 10063, 10009, 10002, 10000, 10009, 10048, 10014, 10027, 10026, 10024,10021, 10020, 10019, 10023, 10025, 10022, 10054, 10056, 10086, 10067, 10078, 10080, 10082, 10076, 10074, 10070, 10011
  // Buraya göstermek istediğiniz metrik ID'lerini ekleyin
];

const MonthlyDataEntry = () => {
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedYear, setSelectedYear] = useState(dayjs().year());
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [monthlyMetrics, setMonthlyMetrics] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [useMetricFilter, setUseMetricFilter] = useState(FILTERED_METRIC_IDS.length > 0);
  
  // Ham veri - filtre açılıp kapatıldığında yeniden çekmeye gerek kalmaması için
  const [rawMetrics, setRawMetrics] = useState([]);
  const [rawMonthsData, setRawMonthsData] = useState([]);
  const [rawMonths, setRawMonths] = useState([]);

  // Marka listesini getir
  const fetchBrands = useCallback(async () => {
    try {
      setInitialLoading(true);
      const response = await api.get('/brands');
      
      // Markaları displayOrder'a göre sırala
      const sortedBrands = response.data.sort((a, b) => {
        const orderA = a.displayOrder || 0;
        const orderB = b.displayOrder || 0;
        return orderA - orderB;
      });
      
      setBrands(sortedBrands);
      
      if (sortedBrands.length > 0) {
        setSelectedBrand(sortedBrands[0].id);
      }
    } catch (error) {
      console.error('Markalar yüklenirken bir hata oluştu:', error);
      message.error('Markalar yüklenirken bir hata oluştu');
    } finally {
      setInitialLoading(false);
    }
  }, []);

  // Seçilen marka değiştiğinde verileri getir
  useEffect(() => {
    if (selectedBrand) {
      fetchYearData();
    }
  }, [selectedBrand, selectedYear]);

  // İlk yükleme
  useEffect(() => {
    fetchBrands();
  }, [fetchBrands]);
  
  // Verileri filtrele ve tablo verilerini oluştur
  const processAndFilterData = useCallback(() => {
    if (rawMetrics.length === 0 || rawMonthsData.length === 0 || rawMonths.length === 0) return;
    
    // Tüm metrikleri al
    let metrics = [...rawMetrics];
    
    // Filtreleme etkinse ve filtrelenecek metrik ID'leri varsa, filtreleme uygula
    if (useMetricFilter && FILTERED_METRIC_IDS.length > 0) {
      metrics = metrics.filter(metric => FILTERED_METRIC_IDS.includes(metric.id));
    }
    
    setMonthlyMetrics(metrics);
    
    // Tablo verisi formatını oluştur
    const tableData = transformToTableData(metrics, rawMonthsData, rawMonths);
    setTableData(tableData);
  }, [rawMetrics, rawMonthsData, rawMonths, useMetricFilter]);
  
  // useMetricFilter değiştiğinde, verileri yeniden filtrele
  useEffect(() => {
    processAndFilterData();
  }, [useMetricFilter, processAndFilterData]);

  // Yıllık metrikleri getir
  const fetchYearData = async () => {
    if (!selectedBrand || !selectedYear) return;
    
    try {
      setLoading(true);
      
      // Seçilen yılın 12 ayı için veri hazırlığı
      const allMonths = [];
      for (let month = 0; month < 12; month++) {
        const date = dayjs(`${selectedYear}-${month + 1}-01`);
        allMonths.push(date.format('YYYY-MM'));
      }
      
      // Tüm aylar için veri çek
      const monthDataPromises = allMonths.map(monthDate => 
        monthlyMetricService.getMonthlyMetricValues(selectedBrand, monthDate)
      );
      
      const monthsData = await Promise.all(monthDataPromises);
      
      // İlk aydan metrik bilgilerini al
      if (monthsData.length > 0 && monthsData[0].length > 0) {
        // Tüm metrikleri al
        const metrics = monthsData[0].map(metric => ({
          id: metric.id,
          name: metric.name,
          description: metric.description,
          isAutoCalculated: metric.isAutoCalculated,
          dataType: metric.dataType,
          color: metric.color || '#FFFFFF',
        }));
        
        // Ham verileri sakla - filtre on/off yapıldığında yeniden çekmeye gerek kalmasın
        setRawMetrics(metrics);
        setRawMonthsData(monthsData);
        setRawMonths(allMonths);
        
        // Verileri filtrele ve tablo için oluştur
        processAndFilterData();
      } else {
        setRawMetrics([]);
        setRawMonthsData([]);
        setRawMonths([]);
        setMonthlyMetrics([]);
        setTableData([]);
      }
    } catch (error) {
      console.error('Yıllık veriler yüklenirken bir hata oluştu:', error);
      message.error('Yıllık veriler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Verileri tablo formatına dönüştür
  const transformToTableData = useCallback((metrics, monthsData, months) => {
    // Tabloyu tarihleri satır, metrikleri sütun olacak şekilde düzenle
    const rows = [];
    
    // Her ay için bir satır oluştur
    months.forEach((month, monthIndex) => {
      const row = {
        monthCode: month,
        monthName: dayjs(month).format('MMMM YYYY')
      };
      
      // Her metrik için bir sütun ekle
      metrics.forEach(metric => {
        // Metrik değerini bul
        const monthDataSet = monthsData[monthIndex] || [];
        const metricValue = monthDataSet.find(m => m.id === metric.id);
        
        // Sütun adı: metricId_NAME formatında 
        // İlerleyen aşamalarda metrik ID ve değer tipi gerekebilir.
        row[`metric_${metric.id}`] = metricValue ? metricValue.value : null;
        
        // İlk satır için metrik bilgilerini ekstra bir nesne olarak sakla
        if (monthIndex === 0) {
          row[`metric_${metric.id}_info`] = {
            isAutoCalculated: metric.isAutoCalculated,
            dataType: metric.dataType,
            name: metric.description || metric.name,
            id: metric.id
          };
        }
      });
      
      rows.push(row);
    });
    
    return rows;
  }, []);

  // Değerleri kaydet
  const saveMetricValue = async (metricId, monthDate, value, isManualOverride) => {
    try {
      await monthlyMetricService.saveMonthlyMetricValue({
        brandId: selectedBrand,
        monthlyMetricId: metricId,
        date: monthDate,
        value: value,
        isManualOverride: isManualOverride
      });
      
      return { success: true };
    } catch (error) {
      console.error('Metrik değeri kaydedilirken hata oluştu:', error);
      return { success: false, error };
    }
  };

  // Değerleri toplu kaydet
  const saveAllChanges = async (changedData) => {
    if (Object.keys(changedData).length === 0) {
      message.info('Değişiklik yapılmadı');
      return;
    }
    
    setLoading(true);
    
    try {
      const savePromises = Object.entries(changedData).map(([key, value]) => {
        // Key formatı: "metricId_YYYY-MM"
        const [metricInfo, monthDate] = key.split('_');
        // metricInfo = "metric123" formatında, metricId'yi çıkar
        const metricId = metricInfo.replace('metric', '');
        
        return saveMetricValue(parseInt(metricId), monthDate, value.newValue, value.isAutoCalculated);
      });
      
      const results = await Promise.all(savePromises);
      
      // Başarılı/başarısız işlem sayısını hesapla
      const successCount = results.filter(r => r.success).length;
      const failCount = results.length - successCount;
      
      if (failCount === 0) {
        message.success(`${successCount} değer başarıyla kaydedildi`);
      } else {
        message.warning(`${successCount} değer kaydedildi, ${failCount} değer kaydedilemedi`);
      }
      
      // Verileri yenile
      fetchYearData();
    } catch (error) {
      console.error('Değerler kaydedilirken bir hata oluştu:', error);
      message.error('Değerler kaydedilirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // CSV template download fonksiyonu
  const downloadCSVTemplate = useCallback(() => {
    if (!monthlyMetrics || monthlyMetrics.length === 0) {
      message.warning('CSV template için veri bulunamadı');
      return;
    }

    // CSV başlıklarını oluştur - metrikler sütunda, aylar satırda
    const headers = ['Ay', ...monthlyMetrics.map(metric => `${metric.id} - ${metric.name || metric.description}`)];
    
    // CSV satırlarını oluştur - her ay için bir satır
    const rows = rawMonths.map(month => {
      const row = [dayjs(month).format('MMM YYYY')];
      
      // Her metrik için değeri ekle (boş bırak - template olduğu için)
      monthlyMetrics.forEach(() => {
        row.push(''); // Boş değer
      });
      
      return row;
    });

    // CSV içeriğini oluştur
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    // Dosyayı indirdir
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `monthly_data_template_${selectedYear}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    message.success('CSV template başarıyla indirildi');
  }, [monthlyMetrics, rawMonths, selectedYear]);

  // CSV upload ve import fonksiyonu
  const handleCSVUpload = useCallback(async (file) => {
    if (!file) return false;

    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        message.error('CSV dosyası geçersiz format');
        return false;
      }

      // İlk satırı başlık olarak al - metrik bilgilerini çıkar
      const headerLine = lines[0];
      const headers = headerLine.split(',').map(col => col.replace(/"/g, '').trim());
      
      // İlk sütun Ay, geri kalanı metrikler
      const metricHeaders = headers.slice(1);
      
      // Metrik ID'lerini başlıklardan çıkar (format: "ID - Name")
      const metricIds = metricHeaders.map(header => {
        const match = header.match(/^(\d+)\s*-/);
        return match ? parseInt(match[1]) : null;
      }).filter(id => id !== null);
      
      // Veri satırlarını işle (her satır bir ay)
      const dataLines = lines.slice(1);
      const imports = [];

      for (const line of dataLines) {
        const columns = line.split(',').map(col => col.replace(/"/g, '').trim());
        
        if (columns.length >= 2) {
          const monthText = columns[0]; // Ay bilgisi
          
          // Ay bilgisini YYYY-MM formatına çevir
          let monthDate = null;
          for (const rawMonth of rawMonths) {
            if (dayjs(rawMonth).format('MMM YYYY') === monthText) {
              monthDate = rawMonth;
              break;
            }
          }
          
          if (monthDate) {
            // Her metrik için değerleri kontrol et
            metricIds.forEach((metricId, index) => {
              const value = columns[index + 1]; // İlk sütunu (ay) atla
              
              if (value && value.trim() !== '') {
                imports.push({
                  metricId,
                  monthDate,
                  value: value.trim()
                });
              }
            });
          }
        }
      }

      if (imports.length === 0) {
        message.warning('CSV dosyasında geçerli veri bulunamadı');
        return false;
      }

      // Verileri kaydet
      setLoading(true);
      let successCount = 0;
      let errorCount = 0;
      
      for (const item of imports) {
        try {
          const result = await saveMetricValue(item.metricId, item.monthDate, item.value, false);
          if (result.success) {
            successCount++;
          } else {
            errorCount++;
          }
        } catch (error) {
          console.error(`Metrik ${item.metricId} (${item.monthDate}) kaydedilemedi:`, error);
          errorCount++;
        }
      }

      if (errorCount === 0) {
        message.success(`${successCount} metrik değeri başarıyla içe aktarıldı`);
      } else {
        message.warning(`${successCount} değer kaydedildi, ${errorCount} değer kaydedilemedi`);
      }
      
      // Verileri yenile
      await fetchYearData();

      return false; // Upload component'inin kendi upload işlemini yapmasını engelle
    } catch (error) {
      console.error('CSV import hatası:', error);
      message.error('CSV dosyası işlenirken hata oluştu');
      return false;
    } finally {
      setLoading(false);
    }
  }, [rawMonths, saveMetricValue, fetchYearData]);

  // Yıl seçenekleri
  const yearOptions = useMemo(() => {
    const currentYear = dayjs().year();
    const startYear = 2020;
    
    const years = [];
    for (let year = startYear; year <= currentYear + 1; year++) {
      years.push(year);
    }
    
    return years;
  }, []);

  // Yükleniyor göstergesi
  if (initialLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <p>Veriler yükleniyor...</p>
      </div>
    );
  }

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={3}>Aylık Metrik Veri Girişi - Yıllık Görünüm</Title>
          
          <Space>
            <Select
              placeholder="Marka Seçin"
              style={{ width: 200 }}
              value={selectedBrand}
              onChange={value => setSelectedBrand(value)}
              loading={initialLoading}
            >
              {brands.map(brand => (
                <Option key={brand.id} value={brand.id}>{brand.name}</Option>
              ))}
            </Select>
            
            <Select
              placeholder="Yıl Seçin"
              style={{ width: 120 }}
              value={selectedYear}
              onChange={value => setSelectedYear(value)}
            >
              {yearOptions.map(year => (
                <Option key={year} value={year}>{year}</Option>
              ))}
            </Select>
            
            {FILTERED_METRIC_IDS.length > 0 && (
              <Space>
                <Text>Filtre:</Text>
                <Switch
                  checkedChildren="Açık"
                  unCheckedChildren="Kapalı"
                  checked={useMetricFilter}
                  onChange={setUseMetricFilter}
                />
              </Space>
            )}
            
            <Tooltip title="Yenile">
              <Button 
                icon={<ReloadOutlined />} 
                onClick={fetchYearData}
                loading={loading}
              />
            </Tooltip>
            
            <Tooltip title="CSV Template İndir">
              <Button
                icon={<DownloadOutlined />}
                onClick={downloadCSVTemplate}
                disabled={!monthlyMetrics || monthlyMetrics.length === 0 || loading}
              />
            </Tooltip>
            
            <Tooltip title="CSV İçe Aktar">
              <Upload
                accept=".csv"
                showUploadList={false}
                beforeUpload={handleCSVUpload}
                disabled={!monthlyMetrics || monthlyMetrics.length === 0 || loading}
              >
                <Button
                  icon={<UploadOutlined />}
                  disabled={!monthlyMetrics || monthlyMetrics.length === 0 || loading}
                />
              </Upload>
            </Tooltip>
          </Space>
        </div>
      </Card>
      
      <Card>
        <YearlyMetricsTable
          data={tableData}
          monthlyMetrics={monthlyMetrics}
          selectedYear={selectedYear}
          loading={loading}
          onSave={saveAllChanges}
        />
      </Card>
    </div>
  );
};

export default MonthlyDataEntry; 